import { ReadingSections } from './reading-sections';
import type { Fact } from '@ni/shared';
import { FactRow } from './fact-row';
import type { ComponentProps, ReactNode } from 'react';
import {
  BRIEF_SECTION_LABELS,
  BRIEF_SECTION_ORDER,
  briefSectionFor,
  WEALTH_BAND_KIND,
} from '@ni/shared';
import {
  FACT_TYPE_LABELS,
  facetLabel,
  NET_WORTH_LABEL,
  PROTECTIVE_READ_HEADS,
  SECTION_NAV,
} from '../../lib/copy';
import { analysisCaveats, analysisProse, clubFitRows } from '../../lib/analysis-copy';
import type { StatusForAnnotation } from '../../lib/copy';
import { FactSections, PanelHead, panelMeta } from './fact-sections';
import { FitBlock } from './fit';
import { ProtectiveReadBlock } from './protective-read';
import { RelationshipBlock } from './relationship';
import { WealthFitBlock } from './wealth-fit';
import { hasNetWorthBand } from './net-worth';
import { Prose } from '../ui/prose';
import './brief.css';
import '../record/record-body.css';

export function notableFacts(facts: Fact[]): Fact[] {
  return facts
    .filter((f) => f.status === 'active' && (f.severity === 'severe' || f.severity === 'moderate'))
    .sort((a, b) => Number(b.severity === 'severe') - Number(a.severity === 'severe'));
}

type Props = ComponentProps<typeof FactSections> & {
  subject: StatusForAnnotation;
  /** Above the panel in the middle column: the questions, the way to start. */
  lead?: ReactNode;
  aside?: ReactNode;
  /** The live run's plan, so sections it has not reached yet show as "not yet". */
  plannedFacets?: string[];
  /** What the Brief group says while it has nothing to show. */
  briefLater?: string | null;
};
const customLabels: Record<string, string> = {
  person_summary: 'Biography',
  club_fit: 'Club fit',
  conversation_notes: 'Conversation notes',
  relationship_read: 'Relationship',
  wealth_fit: 'Fit and interest',
  exposure_brief: PROTECTIVE_READ_HEADS.exposure_brief,
  case_read: PROTECTIVE_READ_HEADS.case_read,
};

// These rollups describe the entire run, not the source or section they were
// copied into. Specific qualifications remain available below, unchanged.
export function sourceNotes(lines: string[]): string[] {
  return lines.filter(
    (line) =>
      !line.startsWith('Current research coverage has limitations: ') &&
      line !==
        'Current research coverage is incomplete. Some planned checks were unavailable, partial or unperformed.',
  );
}

export function ResearchReading({
  subject,
  lead,
  aside,
  plannedFacets = [],
  briefLater = null,
  ...props
}: Props) {
  const { analyses, facts, heldClaims, initialOpen, draft = false } = props;
  const custom = Object.keys(customLabels).filter((kind) =>
    analyses.some((a) => {
      if (a.kind !== kind) return false;
      if (kind === 'person_summary') return Boolean(analysisProse(a));
      if (kind === 'club_fit') {
        const rows = clubFitRows(a);
        return rows.length === 0 || rows.some((row) => row.criterion.toLowerCase() !== 'diligence');
      }
      return true;
    }),
  );
  const orderedFacets = [
    'network',
    ...BRIEF_SECTION_ORDER.filter((s) => s !== 'network'),
  ] as typeof BRIEF_SECTION_ORDER;
  const sectionCount = (section: string) =>
    facts.filter((f) => briefSectionFor(f.fact_type) === section).length +
    heldClaims.filter((f) => briefSectionFor(f.fact_type) === section).length;
  const hasBand = hasNetWorthBand(analyses);
  const facets = orderedFacets.filter(
    (section) => sectionCount(section) > 0 || (section === 'finances' && hasBand),
  );
  const notable = notableFacts(facts);
  const briefKeys = [
    ...custom.filter((k) => k === 'person_summary'),
    ...(notable.length ? ['notable'] : []),
    ...custom.filter((k) => k !== 'person_summary'),
  ];
  const notes = analyses
    .map((a) => ({ a, lines: sourceNotes(analysisCaveats(a)) }))
    .filter((n) => n.lines.length);
  const readingAnalyses = analyses.map((a) => ({
    ...a,
    content: { ...a.content, caveats: [] },
  }));
  const label = (key: string) =>
    (key === 'notable' ? 'Notable findings' : customLabels[key]) ??
    (key === WEALTH_BAND_KIND ? NET_WORTH_LABEL : undefined) ??
    BRIEF_SECTION_LABELS[key as keyof typeof BRIEF_SECTION_LABELS] ??
    key;
  const pending = plannedFacets
    .filter((key) => !facets.includes(key as (typeof facets)[number]))
    .map((key) => ({
      key,
      label: BRIEF_SECTION_LABELS[key as keyof typeof BRIEF_SECTION_LABELS] ?? facetLabel(key),
    }));

  const briefContent = (key: string) => {
    if (key === 'person_summary') {
      const summary = analyses.find((a) => a.kind === key);
      return (
        <div className="r-panel">
          <PanelHead title="Biography" meta={panelMeta(summary, draft)} />
          <div className="reading">
            <Prose text={analysisProse(summary)!} />
          </div>
        </div>
      );
    }
    if (key === 'notable') {
      return (
        <div className="r-panel">
          <PanelHead title="Notable findings" />
          <ul className="rows notable">
            {notable.map((fact) => (
              <FactRow
                key={fact.lineage_id}
                fact={fact}
                subjectId={props.subjectId}
                subjectName={props.subjectName}
                queuedDecisionId={null}
                runId={props.runId}
                anchored={false}
              />
            ))}
          </ul>
          {notable
            .filter((fact) => props.queuedByLineage.has(fact.lineage_id))
            .map((fact) => (
              <p className="src" key={fact.lineage_id}>
                <a className="link" href={`#fact-${fact.lineage_id}`}>
                  Review finding · {FACT_TYPE_LABELS[fact.fact_type]}
                </a>
              </p>
            ))}
        </div>
      );
    }
    return (
      <div className="r-panel">
        <FitBlock analyses={readingAnalyses.filter((a) => a.kind === key)} subject={subject} />
        <RelationshipBlock
          analyses={readingAnalyses.filter((a) => a.kind === key)}
          subject={subject}
        />
        <WealthFitBlock
          analyses={readingAnalyses.filter((a) => a.kind === key)}
          subject={subject}
        />
        {key === 'exposure_brief' || key === 'case_read' ? (
          <ProtectiveReadBlock
            kind={key}
            analyses={readingAnalyses.filter((a) => a.kind === key)}
            subject={subject}
          />
        ) : null}
      </div>
    );
  };

  return (
    <ReadingSections
      initialOpen={initialOpen ?? undefined}
      lead={lead}
      aside={aside}
      pending={pending}
      briefLater={briefLater}
      sections={[
        ...briefKeys.map((key) => ({
          key,
          label: label(key),
          group: 'brief' as const,
          count: key === 'notable' ? notable.length : null,
          content: briefContent(key),
        })),
        ...facets.map((key) => ({
          key,
          label: label(key),
          group: 'research' as const,
          count: sectionCount(key) || null,
          content: <FactSections {...props} analyses={readingAnalyses} sections={[key]} />,
        })),
        ...(notes.length
          ? [
              {
                key: 'coverage',
                label: SECTION_NAV.sourceNotes,
                group: 'notes' as const,
                content: (
                  <div className="r-panel">
                    <PanelHead title="Source notes and limitations" />
                    {notes.map(({ a, lines }) => (
                      <div className="research-source-notes" key={a.id}>
                        <h3>{label(a.kind.replace('facet_summary:', ''))}</h3>
                        <ul>
                          {lines.map((line, i) => (
                            <li key={i}>{line}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                ),
              },
            ]
          : []),
      ]}
    />
  );
}
